import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Package, Receipt, TrendingUp, DollarSign, 
  AlertCircle, Search, Clock, Calendar, Boxes, 
  LayoutDashboard, Activity, Percent, RefreshCw, 
  FileText, Monitor, Wifi, WifiOff, CircleDot,
  AlertTriangle, PackageX, Timer, BarChart3,
  Calculator, RotateCcw, X, Printer, ChevronRight,
  Plug, Check, ExternalLink, Unplug, ChevronDown, Info, Upload
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import CloverExpirationManager from "@/components/CloverExpirationManager";
import CloverWebhookActivity from "@/components/CloverWebhookActivity";
import MerchantHealthScorecard from "@/components/MerchantHealthScorecard";
import Sparkline from "@/components/Sparkline";
import GrocerySettings from "@/components/GrocerySettings";
import RegisterSimulator from "@/components/RegisterSimulator";
import TaxBreakdownWidget from "@/components/TaxBreakdownWidget";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GroceryDashboard() {
  const { businessSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const tabFromUrl = searchParams.get('tab');
  const isDemoMode = searchParams.get('demo') === '1';
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'live');
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Receipt detail modal
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  
  // Date range
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Data states
  const [products, setProducts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);        // 7-day daily [{date,sales,profit,transactions}]
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [tickNow, setTickNow] = useState(Date.now());
  const csvInputRef = useRef(null);

  // Tick every 30s so the "synced 2m ago" text stays fresh without a hard refresh.
  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Small helper — pure function of tickNow so relative timestamps re-render.
  // eslint-disable-next-line no-unused-vars
  const relTime = (iso, _tick = tickNow) => {
    if (!iso) return "";
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 10) return "just now";
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };
  const [alerts, setAlerts] = useState({ low_stock: [], expiring_soon: [] });
  // Register list — populated from Clover /devices on Sync Now.
  // Start empty; the fetchData effect fills it with real device data.
  const [registers, setRegisters] = useState([]);

  // Returns data
  const [returns, setReturns] = useState([
    { id: 'RET-001', receipt_number: 'RCP-1008', date: new Date(), items: [{ name: 'Whole Milk - Gallon', quantity: 1, refund: 4.29, reason: 'Expired' }], total_refund: 4.29, status: 'completed' },
    { id: 'RET-002', receipt_number: 'RCP-1005', date: new Date(Date.now() - 86400000), items: [{ name: 'Doritos Nacho Cheese', quantity: 1, refund: 5.49, reason: 'Damaged' }], total_refund: 5.49, status: 'completed' },
  ]);

  // Tax data
  const [taxData, setTaxData] = useState({
    totalSales: 0, taxableSales: 0, nonTaxableSales: 0, taxCollected: 0, taxRate: 7.5, taxByCategory: []
  });

  const [inventorySearch, setInventorySearch] = useState('');

  // Bumped after AI-powered document imports so the CloverExpirationManager
  // (which caches its own inventory list) reloads and shows the new items.
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0);
  // Last completed AI import summary — surfaces persistent proof-of-work
  // for merchants who dismissed / missed the toast.
  const [lastImportSummary, setLastImportSummary] = useState(null);

  // Clover connection state
  const [cloverStatus, setCloverStatus] = useState(null);
  const [cloverConnecting, setCloverConnecting] = useState(false);

  const fetchCloverStatus = async () => {
    try {
      const res = await fetch(`${API}/api/b/${businessSlug}/clover/status`);
      if (res.ok) setCloverStatus(await res.json());
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchCloverStatus();
  }, [businessSlug]);

  // If user just came back from Clover OAuth, refresh status + show toast feel
  useEffect(() => {
    if (searchParams.get('clover') === 'connected') {
      fetchCloverStatus();
    }
  }, [searchParams]);

  // Developer escape hatch: `?dev_connect=1` on the URL unlocks the Connect
  // Clover button even when production_approved is false, AND forces sandbox
  // environment so we can OAuth against a sandbox test merchant. Real
  // customers never touch this — it's invisible unless the flag is present.
  const isDevConnect = searchParams.get('dev_connect') === '1';

  const handleCloverConnect = async () => {
    setCloverConnecting(true);
    try {
      const res = await fetch(`${API}/api/clover/oauth/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // We hard-force production for real merchants so that a stale/wrong
        // CLOVER_ENVIRONMENT env var on the deploy platform can NEVER
        // send them to Clover's sandbox login (where their real
        // Clover.com password is rejected). Developer escape hatch
        // ?dev_connect=1 flips this to sandbox for internal testing.
        body: JSON.stringify({
          business_slug: businessSlug,
          environment: isDevConnect ? 'sandbox' : 'production',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not start Clover connection. Server may be missing Clover keys.');
        setCloverConnecting(false);
        return;
      }
      const data = await res.json();
      window.location.href = data.auth_url;
    } catch (err) {
      alert('Network error starting Clover connection');
      setCloverConnecting(false);
    }
  };

  const handleCloverDisconnect = async () => {
    if (!window.confirm('Disconnect Clover? This will stop syncing sales data from your POS.')) return;
    try {
      await fetch(`${API}/api/b/${businessSlug}/clover/disconnect`, { method: 'POST' });
      await fetchCloverStatus();
    } catch (err) {
      alert('Failed to disconnect');
    }
  };

  // Sync now — pulls the last 30 days of orders from Clover and upserts them
  // into our grocery_receipts collection so the dashboard reflects live POS
  // data. Re-syncs are idempotent (upserts by clover_order_id).
  const [cloverSyncing, setCloverSyncing] = useState(false);
  const [cloverSyncResult, setCloverSyncResult] = useState(null);
  const handleCloverSyncNow = async () => {
    setCloverSyncing(true);
    setCloverSyncResult(null);
    try {
      // wait=false → the endpoint returns immediately after queueing the sync.
      // This avoids ingress/proxy timeouts on merchants with lots of data.
      // We then poll /sync/status to surface the result to the user.
      const res = await fetch(
        `${API}/api/b/${businessSlug}/clover/sync?lookback_days=30&wait=false`,
        { method: 'POST' },
      );
      const bodyText = await res.text();
      let data = {};
      try { data = JSON.parse(bodyText); } catch { data = {}; }

      if (!res.ok) {
        setCloverSyncResult({
          ok: false,
          summary: data.detail || `Sync failed (HTTP ${res.status}). ${bodyText.slice(0, 120)}`,
          warnings: [],
        });
        setCloverSyncing(false);
        return;
      }

      // Poll sync status for up to ~90s.
      let done = false;
      const startedAt = Date.now();
      while (!done && Date.now() - startedAt < 90000) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await fetch(`${API}/api/b/${businessSlug}/clover/sync/status`);
        if (!s.ok) continue;
        const sJson = await s.json();
        // last_sync_at was updated by the background task once it finishes.
        if (sJson.last_sync_at && sJson.last_sync_at !== cloverStatus?.last_sync_at) {
          const stats = sJson.last_sync_stats || {};
          const warnings = stats.warnings || [];
          const parts = [
            `${stats.upserted ?? 0} order${(stats.upserted ?? 0) === 1 ? '' : 's'}`,
            `${stats.items_synced ?? 0} item${(stats.items_synced ?? 0) === 1 ? '' : 's'}`,
            `${stats.devices_total ?? 0} register${(stats.devices_total ?? 0) === 1 ? '' : 's'}`,
          ];
          setCloverSyncResult({
            ok: warnings.length === 0,
            summary: `Synced ${parts.join(' · ')}`,
            warnings,
          });
          done = true;
        }
      }
      if (!done) {
        setCloverSyncResult({
          ok: false,
          summary: 'Sync is taking longer than expected. It will keep running in the background — check back in a minute.',
          warnings: [],
        });
      }

      await fetchCloverStatus();
      setSearchParams((sp) => { sp.set('synced', Date.now().toString()); return sp; });
    } catch (err) {
      // Surface the actual error message so it's debuggable instead of hiding
      // behind a generic "Network error".
      setCloverSyncResult({
        ok: false,
        summary: `Sync failed: ${err?.message || String(err)}`,
        warnings: [],
      });
    } finally {
      setCloverSyncing(false);
    }
  };

  useEffect(() => {
    if (tabFromUrl) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const res = await fetch(`${API}/api/b/${businessSlug}/settings`);
        if (res.ok) {
          const data = await res.json();
          setBusiness({ name: data.business_info?.name || businessSlug, category: data.category });
        }
      } catch (err) {}
    };
    fetchBusiness();
  }, [businessSlug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, receiptsRes, dashboardRes, alertsRes,
             cloverLowStockRes, cloverExpiringRes, cloverDevicesRes] = await Promise.all([
        fetch(`${API}/api/grocery/b/${businessSlug}/products`),
        fetch(`${API}/api/grocery/b/${businessSlug}/receipts?limit=500`),
        fetch(`${API}/api/grocery/b/${businessSlug}/dashboard?period=month`),
        fetch(`${API}/api/grocery/b/${businessSlug}/alerts`),
        fetch(`${API}/api/b/${businessSlug}/clover/inventory/low-stock`),
        fetch(`${API}/api/b/${businessSlug}/clover/inventory/expiring-soon?days=14`),
        fetch(`${API}/api/b/${businessSlug}/clover/devices`),
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (receiptsRes.ok) {
        const data = await receiptsRes.json();
        setReceipts(data);
        calculateTaxData(data);
      }
      if (dashboardRes.ok) setDashboardStats(await dashboardRes.json());

      // 7-day trend for sparklines — non-blocking, fails silently if endpoint is old
      fetch(`${API}/api/grocery/b/${businessSlug}/sales-trend?days=7`)
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { if (j?.series) setSalesTrend(j.series); })
        .catch(() => {});

      // Merge our own local alerts with Clover-synced inventory alerts.
      // Clover items win when the barcode matches so we don't double-count.
      let baseAlerts = { low_stock: [], expiring_soon: [] };
      if (alertsRes.ok) baseAlerts = await alertsRes.json();
      let cloverLow = [];
      let cloverExpiring = [];
      if (cloverLowStockRes.ok) {
        const j = await cloverLowStockRes.json();
        cloverLow = (j.items || []).map((it) => ({
          id: `clover-${it.clover_item_id}`,
          name: it.name || 'Unnamed item',
          quantity: it.stock_count,
          reorder_threshold: it.reorder_threshold,
          barcode: it.barcode,
          source: 'clover',
        }));
      }
      if (cloverExpiringRes.ok) {
        const j = await cloverExpiringRes.json();
        cloverExpiring = (j.items || []).map((it) => ({
          id: `clover-${it.clover_item_id}`,
          name: it.name || 'Unnamed item',
          expiration_date: it.expiration_date,
          barcode: it.barcode,
          source: 'clover',
        }));
      }
      setAlerts({
        low_stock: [...cloverLow, ...(baseAlerts.low_stock || [])],
        expiring_soon: [...cloverExpiring, ...(baseAlerts.expiring_soon || [])],
      });

      // Real registers from Clover. Once connected, ALWAYS replace the
      // mock with actual Clover data (empty list included) — showing fake
      // "Register 1 ONLINE" while a real merchant is connected is misleading.
      if (cloverDevicesRes.ok) {
        const dj = await cloverDevicesRes.json();
        setRegisters((dj.devices || []).map((d) => ({
          id: d.id,
          name: d.name || 'Register',
          status: d.online ? 'online' : 'offline',
          lastActivity: d.last_seen ? new Date(d.last_seen) : null,
        })));
      }
      setLastRefresh(new Date());
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [businessSlug]);

  const calculateTaxData = (receiptsData) => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59);

    let totalSales = 0, taxableSales = 0, nonTaxableSales = 0, taxCollected = 0;
    const categoryTax = {};

    receiptsData.forEach(receipt => {
      const receiptDate = new Date(receipt.created_at);
      if (receiptDate >= startDate && receiptDate <= endDate) {
        totalSales += receipt.total || 0;
        taxCollected += receipt.tax || 0;
        (receipt.items || []).forEach(item => {
          if (item.taxable) {
            taxableSales += item.total || 0;
            const cat = item.category || 'Other';
            categoryTax[cat] = (categoryTax[cat] || 0) + ((item.total || 0) * 0.075);
          } else {
            nonTaxableSales += item.total || 0;
          }
        });
      }
    });

    setTaxData({
      totalSales, taxableSales, nonTaxableSales, taxCollected, taxRate: 7.5,
      taxByCategory: Object.entries(categoryTax).map(([name, amount]) => ({ name, amount }))
    });
  };

  useEffect(() => {
    if (receipts.length > 0) calculateTaxData(receipts);
  }, [dateRange]);

  const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;
  const formatTime = (date) => new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatShortDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(inventorySearch.toLowerCase()) ||
    p.barcode?.includes(inventorySearch) ||
    p.category?.toLowerCase().includes(inventorySearch.toLowerCase())
  );

  const tabs = [
    { id: 'live', label: 'Live Feed', icon: Activity },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'tax', label: 'Tax Filing', icon: Calculator },
    { id: 'inventory', label: 'Inventory', icon: Boxes },
    { id: 'transactions', label: 'Receipts', icon: Receipt },
    { id: 'returns', label: 'Returns', icon: RotateCcw },
  ];

  // These point at the new amber/zinc design tokens via CSS vars.
  // All in-page tab content uses these strings, so rotating the palette in one
  // place flips every tab at once.
  const cardClass = `hm-card`;
  const textPrimary = `hm-text`;
  const textSecondary = `hm-text-2`;
  const textAccent = `hm-accent`;

  // Receipt Detail Modal
  const ReceiptModal = ({ receipt, onClose }) => {
    if (!receipt) return null;
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div 
          className={`${isDark ? 'bg-slate-800' : 'bg-white'} rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          {/* Receipt Header */}
          <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-bold ${textPrimary}`}>{receipt.receipt_number}</h2>
              <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={`text-sm ${textSecondary} space-y-1`}>
              <p>{formatDate(receipt.created_at)} at {formatTime(receipt.created_at)}</p>
              <p>Register: {receipt.register_id || 'R1'}</p>
              <p>Payment: {receipt.payment_method === 'card' ? '💳 Card' : '💵 Cash'}</p>
            </div>
          </div>
          
          {/* Receipt Items */}
          <div className="p-6">
            <h3 className={`text-sm font-semibold ${textSecondary} mb-3`}>ITEMS</h3>
            <div className="space-y-3">
              {(receipt.items || []).map((item, idx) => (
                <div key={idx} className="flex justify-between items-start">
                  <div>
                    <p className={`font-medium ${textPrimary}`}>{item.name}</p>
                    <p className={`text-sm ${textSecondary}`}>
                      {item.quantity} {item.unit || 'ea'} × {formatCurrency(item.unit_price)}
                      {item.taxable && <span className="ml-2 text-amber-400">+tax</span>}
                    </p>
                  </div>
                  <p className={`font-medium ${textPrimary}`}>{formatCurrency(item.total)}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Receipt Totals */}
          <div className={`p-6 border-t ${isDark ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={textSecondary}>Subtotal</span>
                <span className={textPrimary}>{formatCurrency(receipt.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>Tax (7.5%)</span>
                <span className={textPrimary}>{formatCurrency(receipt.tax)}</span>
              </div>
              <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-slate-600' : 'border-slate-300'}`}>
                <span className={`font-bold ${textPrimary}`}>Total</span>
                <span className={`text-xl font-bold ${textAccent}`}>{formatCurrency(receipt.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'} flex gap-3`}>
            <Button variant="outline" className="flex-1" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="outline" className="flex-1">
              <RotateCcw className="w-4 h-4 mr-2" /> Return
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout businessSlug={businessSlug}>
      <div className="hm-scope min-h-screen">
        <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">

        {/* HEADER — ILKSOY-style */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight hm-text">Dashboard</h1>
            <p className="text-sm hm-text-2 mt-1.5">
              Welcome back to {business?.name || 'your store'}
              <span className="hm-text-3 ml-2">· Last updated {formatTime(lastRefresh)}</span>
            </p>
          </div>
          <button
            onClick={fetchData}
            className="self-start sm:self-end px-4 py-2 text-sm rounded-lg border hm-border hover:hm-border-strong transition-colors flex items-center gap-2 hm-surface hm-text-2 hover:hm-text"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* CLOVER CONNECTION BANNER — ILKSOY-style card with cyan accent corner */}
        {/* Hidden in demo mode so the dashboard looks like an established, connected business. */}
        {cloverStatus && !cloverStatus.connected && !isDemoMode && (
          <div className="hm-card hm-kpi-cyan relative overflow-hidden p-6 sm:p-8" data-testid="clover-connect-banner">
            <div className="absolute top-5 right-5 hm-icon-badge" style={{ backgroundColor: 'var(--hm-accent-soft)' }}>
              <Plug className="w-5 h-5 hm-accent" />
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pr-14 sm:pr-16">
              <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-wider hm-accent font-semibold mb-2">Quick Setup · 2 Minutes</p>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight hm-text mb-2">
                  Connect your Clover register
                </h2>
                <p className="text-sm hm-text-2 leading-relaxed">
                  Sales, taxes, inventory, employees and customers — all pulled in
                  automatically from the front of the store. We do the breakdown,
                  you sip your coffee.
                </p>
                {!cloverStatus.server_ready && (
                  <p className="text-xs mt-3 flex items-center gap-2 hm-warning">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Server is missing Clover API keys. Add them in backend env to unlock.
                  </p>
                )}
                {cloverStatus.server_ready && cloverStatus.production_approved === false && !isDevConnect && (
                  <p className="text-xs mt-3 flex items-center gap-2 hm-warning" data-testid="clover-pending-approval-notice">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Launching soon — our Clover app is pending production approval from Clover.
                    You'll be able to connect your register as soon as it's live.
                  </p>
                )}
                {isDevConnect && (
                  <p className="text-xs mt-3 flex items-center gap-2 text-cyan-600 font-medium" data-testid="clover-dev-connect-notice">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Developer mode: connecting to Clover <strong>sandbox</strong>. Log in with a sandbox test merchant.
                  </p>
                )}
              </div>
              <button
                onClick={handleCloverConnect}
                disabled={cloverConnecting || !cloverStatus.server_ready || (cloverStatus.production_approved === false && !isDevConnect)}
                className="hm-btn-primary shrink-0 px-5 py-2.5 text-sm flex items-center gap-2"
                data-testid="clover-connect-btn"
              >
                {cloverConnecting ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Redirecting…</>
                ) : (cloverStatus.production_approved === false && !isDevConnect) ? (
                  <><Plug className="w-4 h-4" /> Coming Soon</>
                ) : isDevConnect ? (
                  <><Plug className="w-4 h-4" /> Connect Sandbox</>
                ) : (
                  <><Plug className="w-4 h-4" /> Connect Clover</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* CLOVER CONNECTION — connected state (slim status bar) */}
        {cloverStatus && cloverStatus.connected && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-lg border hm-border hm-surface"
            data-testid="clover-status-row"
          >
            <div className="flex items-center gap-3 hm-mono text-xs hm-text-2">
              <span className="w-1.5 h-1.5 rounded-full hm-pulse-dot" style={{ backgroundColor: cloverStatus.environment === 'sandbox' ? 'var(--hm-warning)' : 'var(--hm-success)' }} />
              <span className="uppercase tracking-wider" style={{ color: cloverStatus.environment === 'sandbox' ? 'var(--hm-warning)' : 'var(--hm-success)' }}>
                Clover live
              </span>
              <span className="hidden sm:inline opacity-50">·</span>
              <span className="hidden sm:inline">merchant {cloverStatus.merchant_id?.slice(0, 10)}…</span>
              {cloverStatus.environment === 'sandbox' && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: 'var(--hm-warning)' }}>SANDBOX</span>
              )}
              {cloverStatus.last_sync_at && (
                <>
                  <span className="hidden sm:inline opacity-50">·</span>
                  <span className="hidden sm:inline" title={new Date(cloverStatus.last_sync_at).toLocaleString()}>
                    synced {relTime(cloverStatus.last_sync_at)}
                  </span>
                </>
              )}
              {!cloverStatus.initial_sync_completed && (
                <>
                  <span className="opacity-50">·</span>
                  <span className="hm-warning">initial sync pending</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCloverSyncNow}
                disabled={cloverSyncing}
                className="text-[11px] uppercase tracking-wider hm-text-2 hover:hm-success transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="clover-sync-now-btn"
                title="Pull the last 30 days of orders from Clover"
              >
                <RefreshCw className={`w-3 h-3 ${cloverSyncing ? 'animate-spin' : ''}`} />
                {cloverSyncing ? 'Syncing…' : 'Sync now'}
              </button>
              <span className="opacity-30">|</span>
              <button
                onClick={handleCloverDisconnect}
                className="text-[11px] uppercase tracking-wider hm-text-2 hover:hm-danger transition-colors flex items-center gap-1.5"
                data-testid="clover-disconnect-btn"
              >
                <Unplug className="w-3 h-3" /> Disconnect
              </button>
            </div>
          </div>
        )}

        {/* Sync result — soft pill with collapsible warnings.
             Warnings only show if the user explicitly expands them, so
             the "Imported 1 order" happy path doesn't look alarming. */}
        {cloverSyncResult && (
          <div
            className={`px-3 py-2 rounded-lg text-xs ${
              cloverSyncResult.ok
                ? (isDark ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-700')
                : (isDark ? 'bg-amber-500/10 border border-amber-500/20 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-800')
            }`}
            data-testid="clover-sync-result"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                {cloverSyncResult.ok ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : <Info className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="truncate">{cloverSyncResult.summary || cloverSyncResult.message}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(cloverSyncResult.warnings || []).length > 0 && (
                  <button
                    onClick={() => setWarningsExpanded((v) => !v)}
                    className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100 flex items-center gap-0.5"
                    data-testid="clover-sync-warnings-toggle"
                  >
                    {warningsExpanded ? 'Hide' : 'Details'}
                    <ChevronDown className={`w-3 h-3 transition-transform ${warningsExpanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
                <button onClick={() => setCloverSyncResult(null)} className="opacity-60 hover:opacity-100" aria-label="Dismiss">×</button>
              </div>
            </div>
            {warningsExpanded && (cloverSyncResult.warnings || []).length > 0 && (
              <ul className="mt-2 pl-5 list-disc space-y-0.5 text-[11px] opacity-90 font-mono" data-testid="clover-sync-warnings-list">
                {cloverSyncResult.warnings.map((w, i) => <li key={i} className="break-all">{w}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* In-page tab strip removed — sidebar nav controls activeTab via ?tab= URL param. */}

        {/* LIVE FEED TAB — owner-focused business intelligence */}
        {activeTab === 'live' && (() => {
          // Compute business intelligence from existing data
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayReceipts = receipts.filter(r => new Date(r.created_at) >= today);
          const todaySales = todayReceipts.reduce((s, r) => s + (r.total || 0), 0);
          const todayCost = todayReceipts.reduce((s, r) => s + (r.cost_total || 0), 0);
          const todayProfit = todaySales - todayCost;
          const todayMargin = todaySales > 0 ? (todayProfit / todaySales) * 100 : 0;

          // Yesterday for comparison
          const yest = new Date(today); yest.setDate(yest.getDate() - 1);
          const yestReceipts = receipts.filter(r => {
            const d = new Date(r.created_at);
            return d >= yest && d < today;
          });
          const yestSales = yestReceipts.reduce((s, r) => s + (r.total || 0), 0);
          const salesDelta = yestSales > 0 ? ((todaySales - yestSales) / yestSales) * 100 : 0;

          // Payment mix today
          const cashSales = todayReceipts.filter(r => r.payment_method === 'cash').reduce((s, r) => s + (r.total || 0), 0);
          const cardSales = todayReceipts.filter(r => r.payment_method === 'card').reduce((s, r) => s + (r.total || 0), 0);
          const ebtSales = todayReceipts.filter(r => r.payment_method === 'ebt').reduce((s, r) => s + (r.total || 0), 0);
          const otherSales = todayReceipts.filter(r => !['cash','card','ebt'].includes(r.payment_method)).reduce((s, r) => s + (r.total || 0), 0);
          const totalForMix = cashSales + cardSales + ebtSales + otherSales || 1;

          // Hour-by-hour activity (last 12 hours)
          const hourBuckets = Array.from({ length: 12 }, (_, i) => {
            const h = new Date();
            h.setHours(h.getHours() - (11 - i), 0, 0, 0);
            const next = new Date(h); next.setHours(next.getHours() + 1);
            const inHour = receipts.filter(r => {
              const d = new Date(r.created_at);
              return d >= h && d < next;
            });
            return {
              label: h.toLocaleTimeString('en-US', { hour: 'numeric' }).replace(' ', '').toLowerCase(),
              total: inHour.reduce((s, r) => s + (r.total || 0), 0),
              count: inHour.length,
            };
          });
          const maxHourTotal = Math.max(...hourBuckets.map(b => b.total), 1);

          // Top sellers today (recompute from today's receipts)
          const todaySellersMap = {};
          todayReceipts.forEach(r => {
            (r.items || []).forEach(item => {
              const key = item.product_id || item.name;
              if (!todaySellersMap[key]) todaySellersMap[key] = { name: item.name, qty: 0, revenue: 0, category: item.category };
              todaySellersMap[key].qty += item.quantity || 0;
              todaySellersMap[key].revenue += item.total || 0;
            });
          });
          const topSellersToday = Object.values(todaySellersMap).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

          return (
          <div className="space-y-5">
            {/* KPI ROW — sales / profit / margin / avg ticket  with delta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {[
                {
                  label: "Today's Sales",
                  value: formatCurrency(todaySales),
                  testid: 'todays-sales',
                  variant: 'cyan',
                  icon: DollarSign,
                  sub: yestSales > 0
                    ? <span className={salesDelta >= 0 ? 'hm-success' : 'hm-danger'}>{salesDelta >= 0 ? '▲' : '▼'} {Math.abs(salesDelta).toFixed(1)}% vs yesterday</span>
                    : `${todayReceipts.length} transactions`,
                },
                {
                  label: 'Profit Today',
                  value: formatCurrency(todayProfit),
                  testid: 'profit-today',
                  variant: 'emerald',
                  icon: TrendingUp,
                  sub: `${todayMargin.toFixed(1)}% margin`,
                },
                {
                  label: 'Avg Ticket',
                  value: formatCurrency(todayReceipts.length > 0 ? todaySales / todayReceipts.length : 0),
                  testid: 'avg-ticket',
                  variant: 'blue',
                  icon: Receipt,
                  sub: `${todayReceipts.length} transactions`,
                },
                {
                  label: 'Tax Collected',
                  value: formatCurrency(todayReceipts.reduce((s, r) => s + (r.tax_amount || r.tax || 0), 0)),
                  testid: 'tax-collected',
                  variant: 'amber',
                  icon: Calculator,
                  sub: 'Today',
                },
              ].map((s, idx) => {
                const colorClass = {
                  cyan: { card: 'hm-kpi-cyan', bg: 'rgba(34,211,238,0.18)', text: 'var(--hm-accent)' },
                  blue: { card: 'hm-kpi-blue', bg: 'rgba(96,165,250,0.18)', text: 'var(--hm-blue)' },
                  emerald: { card: 'hm-kpi-emerald', bg: 'rgba(52,211,153,0.18)', text: 'var(--hm-emerald)' },
                  amber: { card: 'hm-kpi-amber', bg: 'rgba(251,191,36,0.18)', text: 'var(--hm-amber)' },
                }[s.variant];
                const Icon = s.icon;
                return (
                  <div key={idx} className={`hm-card ${colorClass.card} relative overflow-hidden p-5`} data-testid={`kpi-${s.testid}-card`}>
                    <div className="absolute top-4 right-4 hm-icon-badge" style={{ backgroundColor: colorClass.bg }}>
                      <Icon className="w-5 h-5" style={{ color: colorClass.text }} />
                    </div>
                    <div className="pr-12">
                      <p className="text-xs hm-text-2 mb-1">{s.label}</p>
                      <p className="text-2xl sm:text-3xl font-bold hm-text" data-testid={`kpi-${s.testid}-value`}>{s.value}</p>
                      <p className="text-xs hm-text-3 mt-1.5">{s.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CRITICAL ALERTS BANNER — only shows when there's something urgent */}
            {(alerts.expiring_soon?.length > 0 || alerts.low_stock?.length > 0) && (
              <div className="hm-card p-4 sm:p-5" style={{ borderLeft: '4px solid var(--hm-danger)' }} data-testid="critical-alerts-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 hm-danger shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold hm-text text-sm mb-2">Needs your attention</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {alerts.expiring_soon?.length > 0 && (
                        <button onClick={() => setActiveTab('inventory')} className="text-left text-xs hm-text-2 hover:hm-text transition-colors">
                          <span className="hm-danger font-bold">{alerts.expiring_soon.length}</span> products expiring within 7 days
                          <span className="block hm-text-3 text-[11px] mt-0.5">Top: {alerts.expiring_soon.slice(0, 3).map(p => p.name).join(', ')}</span>
                        </button>
                      )}
                      {alerts.low_stock?.length > 0 && (
                        <button onClick={() => setActiveTab('inventory')} className="text-left text-xs hm-text-2 hover:hm-text transition-colors">
                          <span className="hm-warning font-bold">{alerts.low_stock.length}</span> products at or below reorder threshold
                          <span className="block hm-text-3 text-[11px] mt-0.5">Top: {alerts.low_stock.slice(0, 3).map(p => p.name).join(', ')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MAIN GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* LEFT COLUMN - 8 wide */}
              <div className="lg:col-span-8 space-y-5">
                {/* HOURLY SALES CHART */}
                <div className="hm-card p-5">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-sm font-semibold hm-text tracking-tight">Hourly sales</h2>
                      <p className="text-xs hm-text-3 mt-0.5">Last 12 hours · ${todaySales.toFixed(0)} total today</p>
                    </div>
                    <BarChart3 className="w-4 h-4 hm-text-3" />
                  </div>
                  <div className="flex items-end gap-1 h-32">
                    {hourBuckets.map((h, i) => {
                      const heightPct = h.total > 0 ? Math.max((h.total / maxHourTotal) * 100, 4) : 0;
                      const isPeak = h.total === maxHourTotal && h.total > 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5" title={`${h.label}: $${h.total.toFixed(2)} · ${h.count} sales`}>
                          <span className="text-[9px] hm-mono hm-text-3 leading-none">{h.total > 0 ? `$${h.total.toFixed(0)}` : ''}</span>
                          <div
                            className="w-full rounded-sm transition-all"
                            style={{
                              height: `${heightPct}%`,
                              backgroundColor: isPeak ? 'var(--hm-accent)' : (h.total > 0 ? 'rgba(34,211,238,0.4)' : 'var(--hm-surface-2)'),
                            }}
                          />
                          <span className="text-[9px] hm-mono hm-text-3 leading-none">{h.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* LIVE TRANSACTIONS */}
                <div className="hm-card flex flex-col" style={{ height: '380px' }}>
                  <div className="flex items-center justify-between px-5 py-4 border-b hm-border sticky top-0 hm-surface z-10 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-semibold hm-text tracking-tight">Live transactions</h2>
                      <span className="text-[10px] uppercase tracking-wider flex items-center gap-1.5 hm-success">
                        <span className="w-1.5 h-1.5 rounded-full hm-pulse-dot hm-bg-success" />
                        Live
                      </span>
                    </div>
                    <span className="text-xs hm-text-2 hm-mono">{todayReceipts.length} today</span>
                  </div>
                  <div className="flex-1 overflow-y-auto no-scrollbar p-1.5">
                    {receipts.slice(0, 30).map((receipt, idx) => (
                      <button
                        key={receipt.id || idx}
                        type="button"
                        onClick={() => setSelectedReceipt(receipt)}
                        className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:hm-surface-2 transition-colors text-left group hm-row-in"
                        style={{ animationDelay: `${idx * 25}ms` }}
                        data-testid={`live-feed-transaction-${receipt.id || idx}`}
                      >
                        <span className="hm-mono text-[11px] hm-text-3 w-14 shrink-0">{formatTime(receipt.created_at)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium hm-text truncate">
                            <span className="hm-mono">{receipt.receipt_number}</span>
                            <span className="ml-2 text-xs hm-text-2 font-normal">{receipt.register_id || 'R1'} · {(receipt.items || []).length} items</span>
                          </p>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded shrink-0 hm-mono"
                          style={{
                            color: receipt.payment_method === 'card' ? 'var(--hm-text-2)' : receipt.payment_method === 'ebt' ? 'var(--hm-blue)' : receipt.payment_method === 'cash' ? 'var(--hm-success)' : 'var(--hm-text-3)',
                            backgroundColor: receipt.payment_method === 'card' ? 'var(--hm-surface-2)' : receipt.payment_method === 'ebt' ? 'rgba(96,165,250,0.10)' : receipt.payment_method === 'cash' ? 'rgba(16,185,129,0.10)' : 'var(--hm-surface-2)',
                          }}>
                          {receipt.payment_method === 'card' ? 'Card' : receipt.payment_method === 'ebt' ? 'EBT' : receipt.payment_method === 'cash' ? 'Cash' : 'Other'}
                        </span>
                        <span className="hm-mono text-sm font-semibold hm-text shrink-0 w-20 text-right">{formatCurrency(receipt.total)}</span>
                        <ChevronRight className="w-4 h-4 hm-text-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </button>
                    ))}
                    {receipts.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-full py-16 hm-text-3">
                        <Activity className="w-8 h-8 opacity-40 mb-3" />
                        <p className="text-xs hm-mono">Waiting for transactions…</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - 4 wide */}
              <div className="lg:col-span-4 space-y-5">
                {/* PAYMENT MIX */}
                <div className="hm-card p-5">
                  <h2 className="text-sm font-semibold hm-text tracking-tight mb-4">Payment mix today</h2>
                  <div className="space-y-3">
                    {[
                      { label: 'Card', amount: cardSales, pct: (cardSales / totalForMix) * 100, color: 'var(--hm-blue)' },
                      { label: 'Cash', amount: cashSales, pct: (cashSales / totalForMix) * 100, color: 'var(--hm-success)' },
                      { label: 'EBT', amount: ebtSales, pct: (ebtSales / totalForMix) * 100, color: 'var(--hm-amber)' },
                      ...(otherSales > 0 ? [{ label: 'Other', amount: otherSales, pct: (otherSales / totalForMix) * 100, color: 'var(--hm-text-3)' }] : []),
                    ].map((p) => (
                      <div key={p.label}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="hm-text font-medium">{p.label}</span>
                          <span className="hm-mono hm-text-2">{formatCurrency(p.amount)} <span className="hm-text-3">· {p.pct.toFixed(0)}%</span></span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--hm-surface-2)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${p.pct}%`, backgroundColor: p.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* TOP SELLERS TODAY */}
                <div className="hm-card p-5">
                  <h2 className="text-sm font-semibold hm-text tracking-tight mb-4 flex items-center justify-between">
                    Top sellers today
                    <TrendingUp className="w-3.5 h-3.5 hm-text-3" />
                  </h2>
                  <div className="space-y-2.5">
                    {topSellersToday.length === 0 ? (
                      <p className="text-xs hm-text-3 text-center py-4">No sales yet today</p>
                    ) : topSellersToday.map((p, i) => (
                      <div key={i} className="flex items-center gap-3" data-testid={`top-seller-${i}`}>
                        <span className="hm-mono text-xs hm-text-3 w-4">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium hm-text truncate">{p.name}</p>
                          <p className="text-[10px] hm-mono hm-text-3">{p.qty} sold · {p.category || 'Other'}</p>
                        </div>
                        <span className="hm-mono text-xs font-semibold hm-text shrink-0">{formatCurrency(p.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* EXPIRY ALERTS */}
                {alerts.expiring_soon?.length > 0 && (
                  <div className="hm-card p-5" data-testid="expiry-alerts-card">
                    <h2 className="text-sm font-semibold hm-text tracking-tight mb-4 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Timer className="w-3.5 h-3.5 hm-danger" />
                        Expiring soon
                      </span>
                      <span className="text-[10px] hm-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.10)', color: 'var(--hm-danger)' }}>
                        {alerts.expiring_soon.length}
                      </span>
                    </h2>
                    <div className="space-y-2">
                      {alerts.expiring_soon.slice(0, 5).map((p, i) => {
                        const days = p.days_until_expiry;
                        const urgent = days <= 2;
                        return (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-md text-xs"
                            style={{ backgroundColor: urgent ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)', borderLeft: `2px solid ${urgent ? 'var(--hm-danger)' : 'var(--hm-warning)'}` }}>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium hm-text truncate">{p.name}</p>
                              <p className="text-[10px] hm-mono hm-text-3">Stock: {p.stock} {p.unit || 'ea'}</p>
                            </div>
                            <span className="hm-mono text-[10px] font-semibold shrink-0" style={{ color: urgent ? 'var(--hm-danger)' : 'var(--hm-warning)' }}>
                              {days <= 0 ? 'TODAY' : days === 1 ? '1 day' : `${days} days`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* LOW STOCK */}
                {alerts.low_stock?.length > 0 && (
                  <div className="hm-card p-5" data-testid="low-stock-card">
                    <h2 className="text-sm font-semibold hm-text tracking-tight mb-4 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <PackageX className="w-3.5 h-3.5 hm-warning" />
                        Reorder needed
                      </span>
                      <span className="text-[10px] hm-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.10)', color: 'var(--hm-warning)' }}>
                        {alerts.low_stock.length}
                      </span>
                    </h2>
                    <div className="space-y-2">
                      {alerts.low_stock.slice(0, 5).map((p, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-md text-xs" style={{ backgroundColor: 'rgba(245,158,11,0.06)', borderLeft: '2px solid var(--hm-warning)' }}>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium hm-text truncate">{p.name}</p>
                            <p className="text-[10px] hm-mono hm-text-3">Reorder at {p.reorder_at} {p.unit || 'ea'}</p>
                          </div>
                          <span className="hm-mono text-[10px] font-semibold hm-warning shrink-0">{p.stock} left</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* REGISTERS — moved here, more compact */}
                <div className="hm-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold hm-text tracking-tight">Registers</h2>
                    <span className="text-[10px] uppercase tracking-wider hm-text-2 hm-mono">
                      {registers.filter(r => r.status === 'online').length}/{registers.length} online
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {registers.length === 0 && (
                      <p className="text-xs hm-text-2" data-testid="registers-empty">
                        No registers detected yet. Add a Clover device or run Sync Now.
                      </p>
                    )}
                    {registers.map((reg) => {
                      const online = reg.status === 'online';
                      return (
                        <div key={reg.id} className="flex items-center justify-between text-xs py-1.5" data-testid={`register-${reg.id}-status`}>
                          <span className="hm-mono hm-text font-medium">{reg.name}</span>
                          <span className="flex items-center gap-1.5">
                            {online ? <Wifi className="w-3 h-3 hm-success" /> : <WifiOff className="w-3 h-3 hm-danger" />}
                            <span className="text-[10px] uppercase tracking-wider" style={{ color: online ? 'var(--hm-success)' : 'var(--hm-danger)' }}>{online ? 'Online' : 'Offline'}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* TAX FILING TAB */}
        {activeTab === 'tax' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Tax Breakdown widget — the "Clover doesn't give you this" split by category */}
            <TaxBreakdownWidget businessSlug={businessSlug} isDark={isDark} />

            {/* Date Range */}
            <div className={`${cardClass} p-4 sm:p-5`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className={`w-4 h-4 sm:w-5 sm:h-5 ${textSecondary}`} />
                  <span className={`font-medium text-sm ${textPrimary}`}>Date Range:</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} className={`w-32 sm:w-40 text-sm ${isDark ? 'bg-slate-700 border-slate-600' : ''}`} />
                  <span className={`text-sm ${textSecondary}`}>to</span>
                  <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} className={`w-32 sm:w-40 text-sm ${isDark ? 'bg-slate-700 border-slate-600' : ''}`} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[{ label: 'Month', days: 30 }, { label: 'Quarter', days: 90 }, { label: 'Year', days: 365 }].map((p) => (
                    <Button key={p.label} variant="outline" size="sm" onClick={() => setDateRange({ start: new Date(Date.now() - p.days * 86400000).toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] })} className={`text-xs ${isDark ? 'border-slate-600' : ''}`}>
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tax Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {[
                { label: 'Total Sales', value: formatCurrency(taxData.totalSales), icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                { label: 'Taxable Sales', value: formatCurrency(taxData.taxableSales), icon: Percent, color: 'text-amber-500', bg: 'bg-amber-500/10', sub: `@ ${taxData.taxRate}%` },
                { label: 'Tax Collected', value: formatCurrency(taxData.taxCollected), icon: Calculator, color: 'text-blue-500', bg: 'bg-blue-500/10', sub: 'Amount to file' },
                { label: 'Non-Taxable', value: formatCurrency(taxData.nonTaxableSales), icon: Receipt, color: 'text-green-500', bg: 'bg-green-500/10', sub: 'Tax exempt' },
              ].map((s, i) => (
                <div key={i} className={`${cardClass} p-4 sm:p-6`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                      <s.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${s.color}`} />
                    </div>
                    <span className={`text-xs sm:text-sm ${textSecondary}`}>{s.label}</span>
                  </div>
                  <p className={`text-2xl sm:text-4xl font-bold ${s.color}`}>{s.value}</p>
                  {s.sub && <p className={`text-xs ${textSecondary} mt-1 sm:mt-2`}>{s.sub}</p>}
                </div>
              ))}
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className={cardClass}>
                <div className="p-4 sm:p-5 border-b border-slate-700/30">
                  <h3 className={`text-base sm:text-lg font-semibold ${textPrimary}`}>Tax Filing Summary</h3>
                </div>
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-5">
                  {[
                    { label: 'Total Gross Sales', value: formatCurrency(taxData.totalSales) },
                    { label: 'Less: Non-Taxable', value: `- ${formatCurrency(taxData.nonTaxableSales)}` },
                    { label: 'Taxable Sales', value: formatCurrency(taxData.taxableSales) },
                    { label: 'Tax Rate (Ohio)', value: `${taxData.taxRate}%` },
                  ].map((r, i) => (
                    <div key={i} className={`flex justify-between items-center py-2 sm:py-3 border-b ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
                      <span className={`text-sm ${textSecondary}`}>{r.label}</span>
                      <span className={`text-base sm:text-xl ${textPrimary}`}>{r.value}</span>
                    </div>
                  ))}
                  <div className={`flex justify-between items-center py-3 sm:py-4 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'} px-3 sm:px-4 -mx-2`}>
                    <span className={`font-semibold ${textPrimary}`}>TAX DUE</span>
                    <span className="text-xl sm:text-2xl font-bold text-blue-500">{formatCurrency(taxData.taxCollected)}</span>
                  </div>
                </div>
              </div>

              <div className={cardClass}>
                <div className="p-4 sm:p-5 border-b border-slate-700/30">
                  <h3 className={`text-base sm:text-lg font-semibold ${textPrimary}`}>Tax by Category</h3>
                </div>
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  {taxData.taxByCategory.length > 0 ? taxData.taxByCategory.map((cat, i) => (
                    <div key={i} className="flex justify-between py-2">
                      <span className={`text-sm ${textPrimary}`}>{cat.name}</span>
                      <span className={`font-semibold ${textAccent}`}>{formatCurrency(cat.amount)}</span>
                    </div>
                  )) : <p className={`text-center py-6 sm:py-8 text-sm ${textSecondary}`}>No taxable sales</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* INVENTORY TAB */}
        {activeTab === 'inventory' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Smart AI-powered inventory import — accepts CSV, PDF, Word,
                Excel, TXT, notes, images. The LLM extracts items from any
                document with writing in it. */}
            <Card className={isDark ? "bg-slate-800 border-slate-700" : ""} data-testid="clover-csv-import-card">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Upload className={`w-5 h-5 flex-shrink-0 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-800"}`}>
                    Import items from ANY document
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-mono ${isDark ? "bg-cyan-500/20 text-cyan-300" : "bg-cyan-100 text-cyan-700"}`}>
                      AI-powered
                    </span>
                  </p>
                  <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Upload <strong>CSV, Excel, PDF, Word doc, text file, or notes</strong> — even a photo of a handwritten inventory list.
                    Our AI reads it, extracts every item with name/price/cost/stock/category, and drops them straight into your inventory. Review + edit after.
                  </p>
                </div>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xls,.pdf,.doc,.docx,.rtf,.jpg,.jpeg,.png,.webp,.heic,text/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const fd = new FormData();
                    fd.append("file", f);
                    // Kick off async job — returns immediately with job_id
                    const toastId = toast.loading(`Uploading ${f.name}…`);
                    let jobId;
                    try {
                      const submit = await axios.post(
                        `${API}/api/grocery/b/${businessSlug}/clover/import-anything`,
                        fd,
                        { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }
                      );
                      jobId = submit.data.job_id;
                    } catch (err) {
                      toast.dismiss(toastId);
                      toast.error(err?.response?.data?.detail || "Upload failed", { duration: 8000 });
                      e.target.value = "";
                      return;
                    }
                    // Poll for completion. AI extraction runs in the background
                    // on the server — we poll every 2s up to ~2 minutes.
                    toast.loading("AI is reading your file…", { id: toastId });
                    const start = Date.now();
                    const poll = async () => {
                      if (Date.now() - start > 120000) {
                        toast.dismiss(toastId);
                        toast.error("AI extraction is taking longer than 2 minutes — check back later on the Inventory table.", { duration: 10000 });
                        return;
                      }
                      try {
                        const s = await axios.get(
                          `${API}/api/grocery/b/${businessSlug}/clover/import-anything/status/${jobId}`,
                          { timeout: 15000 }
                        );
                        if (s.data.status === "complete") {
                          toast.dismiss(toastId);
                          const imported = s.data.imported || 0;
                          const sampleNames = (s.data.sample_preview || [])
                            .map((x) => x?.name)
                            .filter(Boolean)
                            .slice(0, 3)
                            .join(", ");
                          if (imported === 0) {
                            const aiSaid =
                              s.data?.ai_debug?.second_pass_raw ||
                              s.data?.ai_debug?.first_pass_raw ||
                              "";
                            toast.warning(
                              `AI couldn't extract items from ${f.name}. See the "Last import" panel for what the AI actually saw.`,
                              { duration: 12000 }
                            );
                            setLastImportSummary({
                              filename: f.name,
                              imported: 0,
                              sample: "",
                              aiSaid: aiSaid.slice(0, 500),
                              at: new Date().toISOString(),
                            });
                          } else {
                            toast.success(
                              `Imported ${imported} items from ${f.name}${sampleNames ? ` — e.g. ${sampleNames}` : ""}`,
                              { duration: 10000 }
                            );
                            setLastImportSummary({
                              filename: f.name,
                              imported,
                              sample: sampleNames,
                              aiSaid: "",
                              at: new Date().toISOString(),
                            });
                          }
                          setInventoryRefreshKey((k) => k + 1);
                          fetchData();
                          return;
                        }
                        if (s.data.status === "failed") {
                          toast.dismiss(toastId);
                          toast.error(`Import failed: ${s.data.error || "unknown error"}`, { duration: 10000 });
                          return;
                        }
                      } catch { /* transient error — keep polling */ }
                      setTimeout(poll, 2000);
                    };
                    poll();
                    e.target.value = "";
                  }}
                  data-testid="clover-csv-import-input"
                />
                <Button
                  size="sm"
                  onClick={() => csvInputRef.current?.click()}
                  className="flex-shrink-0"
                  data-testid="clover-csv-import-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload file
                </Button>
              </CardContent>
              {lastImportSummary && (
                <div
                  className={`px-4 pb-3 -mt-1 text-xs flex flex-col gap-1.5 ${isDark ? "text-slate-300" : "text-slate-600"}`}
                  data-testid="clover-csv-import-last-summary"
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${lastImportSummary.imported > 0 ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <span>
                      Last import: <strong>{lastImportSummary.imported}</strong> items from{" "}
                      <span className="font-mono">{lastImportSummary.filename}</span>
                      {lastImportSummary.sample && <> — {lastImportSummary.sample}</>}
                    </span>
                  </div>
                  {lastImportSummary.imported === 0 && lastImportSummary.aiSaid && (
                    <div
                      className={`ml-3.5 mt-1 p-2 rounded border text-[11px] font-mono whitespace-pre-wrap max-h-32 overflow-auto ${isDark ? "bg-slate-900/60 border-slate-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                      data-testid="clover-csv-import-ai-response"
                    >
                      <div className={`text-[10px] mb-1 uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        What the AI saw:
                      </div>
                      {lastImportSummary.aiSaid}
                    </div>
                  )}
                </div>
              )}
            </Card>
            <CloverExpirationManager
              businessSlug={businessSlug}
              isDark={isDark}
              onSaved={fetchData}
              refreshKey={inventoryRefreshKey}
            />
            <CloverWebhookActivity businessSlug={businessSlug} isDark={isDark} />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-1">
                <Search className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${textSecondary}`} />
                <Input placeholder="Search products..." value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} className={`pl-10 sm:pl-12 py-4 sm:py-6 text-sm sm:text-lg ${isDark ? 'bg-slate-800 border-slate-700' : ''}`} />
              </div>
              <div className="flex gap-2 sm:gap-3">
                <div className={`${cardClass} px-3 sm:px-5 py-2 sm:py-3 flex items-center gap-2`}>
                  <span className={`text-xs sm:text-sm ${textSecondary}`}>Products:</span>
                  <span className={`font-bold text-sm sm:text-base ${textPrimary}`}>{products.length}</span>
                </div>
                <div className={`${cardClass} px-3 sm:px-5 py-2 sm:py-3 flex items-center gap-2`}>
                  <span className={`text-xs sm:text-sm ${textSecondary}`}>Low Stock:</span>
                  <span className="font-bold text-sm sm:text-base text-amber-500">{alerts.low_stock?.length || 0}</span>
                </div>
              </div>
            </div>

            <div className={`${cardClass} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                    <tr>
                      {['PRODUCT', 'BARCODE', 'CATEGORY', 'BUY', 'SELL', 'PROFIT', 'MARGIN', 'STOCK', 'STATUS'].map((h) => (
                        <th key={h} className={`text-left p-2 sm:p-4 text-xs font-semibold ${textSecondary} whitespace-nowrap`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p, i) => {
                      const profit = (p.price || 0) - (p.cost || 0);
                      const margin = p.price ? ((profit / p.price) * 100).toFixed(0) : 0;
                      const low = p.stock <= (p.reorder_at || 10);
                      return (
                        <tr key={p.id || i} className={`border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}>
                          <td className={`p-2 sm:p-4 ${textPrimary} text-sm`}>{p.name}</td>
                          <td className={`p-2 sm:p-4 font-mono text-xs ${textSecondary}`}>{p.barcode || '-'}</td>
                          <td className="p-2 sm:p-4"><Badge variant="outline" className={`text-xs ${isDark ? 'border-slate-600' : ''}`}>{p.category || 'General'}</Badge></td>
                          <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{formatCurrency(p.cost)}</td>
                          <td className={`p-2 sm:p-4 text-xs sm:text-sm font-semibold ${textPrimary}`}>{formatCurrency(p.price)}</td>
                          <td className={`p-2 sm:p-4 text-xs sm:text-sm font-semibold ${profit > 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(profit)}</td>
                          <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{margin}%</td>
                          <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textPrimary}`}>{p.stock} {p.unit || 'ea'}</td>
                          <td className="p-2 sm:p-4">{low ? <Badge variant="destructive" className="text-xs">Low</Badge> : <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">OK</Badge>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredProducts.length === 0 && (
                  <div className={`p-8 sm:p-12 text-center ${textSecondary}`}>
                    <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">{inventorySearch ? 'No results' : 'No products'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TRANSACTIONS/RECEIPTS TAB */}
        {activeTab === 'transactions' && (
          <div className={`${cardClass} overflow-hidden`}>
            <div className="p-4 sm:p-5 border-b border-slate-700/30">
              <h3 className={`text-base sm:text-lg font-semibold ${textPrimary}`}>All Receipts</h3>
              <p className={`text-xs sm:text-sm ${textSecondary} mt-1`}>Click any receipt to view full details</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                  <tr>
                    {['RECEIPT', 'DATE', 'REGISTER', 'ITEMS', 'SUBTOTAL', 'TAX', 'TOTAL', 'PAYMENT'].map((h) => (
                      <th key={h} className={`text-left p-2 sm:p-4 text-xs font-semibold ${textSecondary} whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r, i) => (
                    <tr key={r.id || i} onClick={() => setSelectedReceipt(r)} className={`border-t ${isDark ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-100 hover:bg-slate-50'} cursor-pointer transition-colors`}>
                      <td className={`p-2 sm:p-4 font-medium text-sm ${textPrimary}`}>{r.receipt_number}</td>
                      <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{formatShortDate(r.created_at)} {formatTime(r.created_at)}</td>
                      <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textPrimary}`}>{r.register_id || 'R1'}</td>
                      <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{(r.items || []).length}</td>
                      <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{formatCurrency(r.subtotal)}</td>
                      <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{formatCurrency(r.tax)}</td>
                      <td className={`p-2 sm:p-4 text-sm font-semibold ${textPrimary}`}>{formatCurrency(r.total)}</td>
                      <td className="p-2 sm:p-4"><Badge variant={r.payment_method === 'card' ? 'default' : 'secondary'} className="text-xs">{r.payment_method === 'card' ? '💳' : '💵'}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {receipts.length === 0 && (
                <div className={`p-8 sm:p-12 text-center ${textSecondary}`}>
                  <Receipt className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No receipts</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RETURNS TAB */}
        {activeTab === 'returns' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Returns Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {[
                { label: 'Total Returns', value: returns.length, color: 'text-red-500' },
                { label: 'Refunded', value: formatCurrency(returns.reduce((s, r) => s + r.total_refund, 0)), color: 'text-amber-500' },
                { label: 'This Week', value: returns.filter(r => new Date(r.date) > new Date(Date.now() - 7 * 86400000)).length, color: 'text-blue-500' },
                { label: 'Pending', value: returns.filter(r => r.status === 'pending').length, color: 'text-purple-500' },
              ].map((s, i) => (
                <div key={i} className={`${cardClass} p-3 sm:p-5`}>
                  <p className={`text-xs ${textSecondary} mb-1`}>{s.label}</p>
                  <p className={`text-xl sm:text-3xl font-bold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Returns List */}
            <div className={`${cardClass} overflow-hidden`}>
              <div className="p-4 sm:p-5 border-b border-slate-700/30">
                <h3 className={`text-base sm:text-lg font-semibold ${textPrimary}`}>Return History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                    <tr>
                      {['RETURN ID', 'ORIGINAL RECEIPT', 'DATE', 'ITEMS', 'REASON', 'REFUND', 'STATUS'].map((h) => (
                        <th key={h} className={`text-left p-2 sm:p-4 text-xs font-semibold ${textSecondary} whitespace-nowrap`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {returns.map((ret, i) => (
                      <tr key={ret.id || i} className={`border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}>
                        <td className={`p-2 sm:p-4 font-medium text-sm ${textPrimary}`}>{ret.id}</td>
                        <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{ret.receipt_number}</td>
                        <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{formatShortDate(ret.date)}</td>
                        <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textPrimary}`}>{ret.items.map(i => i.name).join(', ')}</td>
                        <td className={`p-2 sm:p-4 text-xs sm:text-sm ${textSecondary}`}>{ret.items[0]?.reason || '-'}</td>
                        <td className={`p-2 sm:p-4 text-sm font-semibold text-red-500`}>-{formatCurrency(ret.total_refund)}</td>
                        <td className="p-2 sm:p-4">
                          <Badge variant={ret.status === 'completed' ? 'default' : 'secondary'} className={`text-xs ${ret.status === 'completed' ? 'bg-green-500/20 text-green-400' : ''}`}>
                            {ret.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {returns.length === 0 && (
                  <div className={`p-8 sm:p-12 text-center ${textSecondary}`}>
                    <RotateCcw className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No returns</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Merchant Health Scorecard — headline widget */}
            <MerchantHealthScorecard businessSlug={businessSlug} isDark={isDark} />

            {/* KPI cards with 7-day sparkline trends */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
              {(() => {
                const salesSeries  = salesTrend.map((d) => d.sales);
                const costSeries   = salesTrend.map((d) => Math.max(0, (d.sales || 0) - (d.profit || 0)));
                const profitSeries = salesTrend.map((d) => d.profit);
                const txnSeries    = salesTrend.map((d) => d.transactions);
                const cards = [
                  { label: 'Total Sales',   value: formatCurrency(dashboardStats?.total_sales),  color: 'text-emerald-500', series: salesSeries,  spark: '#10b981' },
                  { label: 'Cost of Goods', value: formatCurrency(dashboardStats?.total_cost),   color: 'text-blue-500',    series: costSeries,   spark: '#3b82f6' },
                  { label: 'Gross Profit',  value: formatCurrency(dashboardStats?.total_profit), color: 'text-purple-500',  series: profitSeries, spark: '#a855f7' },
                  { label: 'Transactions',  value: dashboardStats?.transaction_count || 0,       color: 'text-amber-500',   series: txnSeries,    spark: '#f59e0b' },
                  { label: 'Low Stock',     value: alerts.low_stock?.length || 0,                color: 'text-red-500',     series: null,         spark: '#ef4444' },
                  { label: 'Expiring',      value: alerts.expiring_soon?.length || 0,            color: 'text-orange-500',  series: null,         spark: '#f97316' },
                ];
                return cards.map((s, i) => (
                  <div key={i} className={`${cardClass} p-3 sm:p-5 flex flex-col justify-between min-h-[92px]`}>
                    <div>
                      <p className={`text-xs ${textSecondary} mb-1 sm:mb-2`}>{s.label}</p>
                      <p className={`text-lg sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
                    </div>
                    {s.series && (
                      <div className="mt-2 -mx-1" style={{ color: s.spark }}>
                        <Sparkline data={s.series} width={100} height={22} stroke={s.spark} fill={s.spark} ariaLabel={`${s.label} 7-day trend`} />
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>

            <div className={cardClass}>
              <div className="p-4 sm:p-5 border-b border-slate-700/30">
                <h3 className={`text-base sm:text-lg font-semibold ${textPrimary}`}>Sales by Category</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {Object.entries(dashboardStats?.category_sales || {}).map(([cat, amt], i) => (
                  <div key={i}>
                    <div className="flex justify-between mb-1 sm:mb-2">
                      <span className={`text-sm ${textPrimary}`}>{cat}</span>
                      <span className={`text-sm font-semibold ${textAccent}`}>{formatCurrency(amt)}</span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min((amt / (dashboardStats?.total_sales || 1)) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
                {Object.keys(dashboardStats?.category_sales || {}).length === 0 && (
                  <div className={`text-center py-8 sm:py-10 ${textSecondary}`}>
                    <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No category data yet</p>
                    <p className="text-xs mt-1 opacity-70">Sales get bucketed by category once items are categorized in Clover or in the Inventory tab.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* REGISTER (in-app simulator) TAB */}
        {activeTab === 'register' && (
          <RegisterSimulator
            businessSlug={businessSlug}
            isDark={isDark}
            taxRate={dashboardStats?.tax_rate || 0}
          />
        )}

        {/* SETTINGS TAB — grocery-specific config (replaces the generic /settings page) */}
        {activeTab === 'settings' && (
          <GrocerySettings businessSlug={businessSlug} isDark={isDark} />
        )}

        {/* Receipt Detail Modal */}
        {selectedReceipt && <ReceiptModal receipt={selectedReceipt} onClose={() => setSelectedReceipt(null)} />}
        </div>
      </div>
    </Layout>
  );
}
